import type { StateCreator } from 'zustand';
import type { DateRange } from 'react-day-picker';
import { setHours, setMinutes, setSeconds, addMinutes } from 'date-fns';
import type { Service } from '../../../typings/common/booking';

export interface DateTimeSlice {
	// State - Range is the single source of truth
	selectedRange: DateRange | undefined;

	/**
	 * Stores the original booking range when editing an entry.
	 * This is used to display the "before editing" state and highlight
	 * the original date/time in the UI.
	 */
	originalRange: DateRange | undefined;

	/**
	 * Tracks whether the store has been initialized from the hidden input value.
	 * This prevents race conditions in multi-page forms where useBookingTimeValue
	 * would compute an empty string and overwrite the hidden input before the
	 * initialize() function could restore the saved state from the hidden input.
	 */
	isInitialized: boolean;

	// Actions
	setSelectedRange: (range: DateRange | undefined) => void;
	reset: () => void;

	// Initialization from hidden input
	initialize: (initialValue: string, service: Service) => void;

	// Convenience setters - update selectedRange
	setSelectedDate: (date: Date | undefined) => void;
	setSelectedTime: (time: string | undefined) => void;
	setSelectedDuration: (duration: number) => void;
}

export const createDateTimeSlice: StateCreator<DateTimeSlice> = (set, get) => ({
	selectedRange: undefined,
	originalRange: undefined,
	isInitialized: false,

	setSelectedRange: (range) =>
		set({ selectedRange: range, isInitialized: true }),

	reset: () =>
		set({
			selectedRange: undefined,
			originalRange: undefined,
			isInitialized: false,
		}),

	initialize: (initialValue: string, service: Service) => {
		if (!service) {
			return;
		}

		if (!initialValue) {
			set({ isInitialized: true });
			return;
		}

		try {
			// Parse the range format: "start|end" or single "datetime"
			const [startStr, endStr] = initialValue.includes('|')
				? initialValue.split('|')
				: [initialValue, initialValue];

			const from = new Date(startStr);
			const to = new Date(endStr || startStr);

			set({
				selectedRange: { from, to },
				originalRange: { from, to }, // Store original for entry edit mode
				isInitialized: true,
			});
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error(
				'Failed to initialize booking state:',
				initialValue,
				e
			);
			set({ isInitialized: true });
		}
	},

	// Convenience setters - update selectedRange
	setSelectedDate: (date) => {
		const currentRange = get().selectedRange;
		if (!date) {
			set({ selectedRange: undefined, isInitialized: true });
			return;
		}

		// Preserve time from existing range, or use midnight
		let from = new Date(date);
		if (currentRange?.from) {
			from = setHours(from, currentRange.from.getHours());
			from = setMinutes(from, currentRange.from.getMinutes());
			from = setSeconds(from, 0);
		}

		// If we have an end time, preserve the duration
		let to = from;
		if (currentRange?.to && currentRange?.from) {
			const durationMinutes = Math.round(
				(currentRange.to.getTime() - currentRange.from.getTime()) /
					(1000 * 60)
			);
			to = addMinutes(from, durationMinutes);
		}

		set({ selectedRange: { from, to }, isInitialized: true });
	},

	setSelectedTime: (time) => {
		const currentRange = get().selectedRange;
		if (!time) {
			set({ selectedRange: undefined, isInitialized: true });
			return;
		}

		// Parse time and update the from date using date-fns
		const [hours, mins] = time.split(':').map(Number);
		let from = currentRange?.from
			? new Date(currentRange.from)
			: new Date();
		from = setHours(from, hours);
		from = setMinutes(from, mins);
		from = setSeconds(from, 0);

		// Preserve duration if we have an end time
		let to = from;
		if (currentRange?.to && currentRange?.from) {
			const durationMinutes = Math.round(
				(currentRange.to.getTime() - currentRange.from.getTime()) /
					(1000 * 60)
			);
			to = addMinutes(from, durationMinutes);
		}

		set({ selectedRange: { from, to }, isInitialized: true });
	},

	setSelectedDuration: (duration) => {
		const currentRange = get().selectedRange;
		if (!currentRange?.from) {
			return;
		}

		const to = addMinutes(currentRange.from, duration);
		set({
			selectedRange: { from: currentRange.from, to },
			isInitialized: true,
		});
	},
});
